import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { company } from '~/company/Company';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { useStores } from '~/hooks/useStore';
import { heroBannerStore } from '~/stores/HeroBannerStore';
import resetScroll from '~/utils/resetScroll';

import AuthorizationGuard from '../../components/AuthorizationGuard/AuthorizationGuard';
import { useGlobal } from '../../hooks/useGlobal';

import Address from '../Address/Address';
import Cabinet from '../Cabinet/Cabinet';
import Cart from '../Cart/Cart';
import CartV3 from '../CartV3';
import Catalog from '../Catalog/Catalog';
import { CatalogV3 } from '../Catalog/components/CatalogV3';
import Brand from '../CD/Brand';
import Careers from '../CD/Careers';
import ContactUs from '../CD/ContactUs/ContactUs';
import FAQ from '../CD/FAQ';
import Influencers from '../CD/Influencers';
import OurStory from '../CD/OurStory';
import Refund from '../CD/Refund';
import ShippingReturn from '../CD/ShippingReturn';
import Checkout from '../Checkout/Checkout';
import AboutDE from '../DubaiExpress/AboutPage/AboutPage';
import ContactsDE from '../DubaiExpress/ContactsPage/ContactsPage';
import DeliveryDE from '../DubaiExpress/DeliveryPage/DeliveryPage';
import FAQDE from '../DubaiExpress/FAQPage/FAQPage';
import ReturnsDE from '../DubaiExpress/ReturnsPage/ReturnsPage';
import Events from '../Events/Events';
import Favs from '../Favs/Favs';
import Icons from '../Icons/Icons';
import Legals from '../Legals/Legals';
import LoyaltyProgram from '../LoyaltyProgram/LoyaltyProgram';
import Main from '../Main/Main';
import MainV3 from '../MainV3/MainV3';
import NotFound from '../NotFound/NotFound';
import { About } from '../Okie/About/About';
import { CustomerCare } from '../Okie/CustomerCare/CustomerCare';
import { FAQ as FaqOkie } from '../Okie/FAQ/FAQ';
import { Payment } from '../Okie/Payment/Payment';
import OrderPage from '../Order/Order';
import { OrderPaymentPage } from '../OrderPayment';
import Orders from '../Orders/Orders';
import { PaymentPage } from '../PaymentMethod/PaymentMethod';
import PersonalData from '../PersonalData/PersonalData';
import Product from '../Product/Product';
import Promocodes from '../Promocodes';
import ReferralProgram from '../ReferralProgram/ReferralProgram';
import RewardedPromocodes from '../RewardedPromocodes/RewardedPromocodes';

import { SearchResults } from '../SearchResults';
import Shop from '../Shop/Shop';
import Subscription from '../Subscription/Subscription';

import { ROUTES } from './constants';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';

export const NOT_FOUND_PAGE = '/not-found';

export default observer(() => {
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const layoutRef = useRef<HTMLDivElement>(null);
  const { shopCategories, homeCategories } = useStores();

  const LayoutComponent = isMobile ? MobileLayout : DesktopLayout;

  useEffect(() => {
    if (layoutRef?.current) {
      resetScroll({ isMobile });
    }
  }, [pathname, layoutRef]);

  useOnWarehouseChange(
    (code, isChanged) => {
      if (isChanged || !shopCategories.list.length) {
        shopCategories.request.run(code);
      }
    },
    [shopCategories],
  );

  useOnWarehouseChange(
    (code, isChanged) => {
      if (isChanged || !homeCategories.list.length) {
        homeCategories.paginationRequest.loadPage(1, code);
        if (pathname === ROUTES.index) {
          resetScroll({ isMobile });
        }
      }
    },
    [homeCategories],
  );

  useOnWarehouseChange(
    (code, isChanged) => {
      if (isChanged || !heroBannerStore.isLoaded) {
        heroBannerStore.load(code);
      }
    },
    [heroBannerStore],
  );

  const CatalogComponent = company.newDesignSystemEnabled ? CatalogV3 : Catalog;

  return (
    <>
      <LayoutComponent layoutRef={layoutRef}>
        <Routes>
          <Route
            path={ROUTES.index}
            element={company.newDesignSystemEnabled ? <MainV3 /> : <Main />}
          />
          <Route path={ROUTES.catalog} element={<CatalogComponent />} />
          <Route path={ROUTES.shop} element={<Shop />} />
          <Route path={ROUTES.product} element={<Product />} />
          {/* TODO: It should be in /cabinet scope. */}
          {!company.hideCabinetRoutes && (
            <>
              <Route path={ROUTES.favs} element={<Favs />} />
              <Route
                path={ROUTES.cart}
                element={company.newDesignSystemEnabled ? <CartV3 /> : <Cart />}
              />
              <Route
                path={ROUTES.checkout}
                element={
                  <AuthorizationGuard>
                    <Checkout />
                  </AuthorizationGuard>
                }
              />
              <Route
                path={ROUTES.order}
                element={
                  <AuthorizationGuard>
                    <OrderPage />
                  </AuthorizationGuard>
                }
              />
              {company.isMobileMoneyPaymentEnabled && (
                <Route
                  path={ROUTES.order_payment}
                  element={
                    <AuthorizationGuard>
                      <OrderPaymentPage />
                    </AuthorizationGuard>
                  }
                />
              )}
              <Route
                path={ROUTES.loyalty}
                element={
                  <AuthorizationGuard requireVerified>
                    <LoyaltyProgram />
                  </AuthorizationGuard>
                }
              />
              <Route path={ROUTES.cabinet}>
                <Route
                  index
                  element={
                    <AuthorizationGuard>
                      <Cabinet />
                    </AuthorizationGuard>
                  }
                />
                <Route path={ROUTES.cabinet_orders}>
                  <Route
                    index
                    element={
                      <AuthorizationGuard>
                        <Orders />
                      </AuthorizationGuard>
                    }
                  />
                  <Route
                    path={ROUTES.cabinet_orders_tab}
                    element={
                      <AuthorizationGuard>
                        <Orders />
                      </AuthorizationGuard>
                    }
                  />
                </Route>
                <Route
                  path={ROUTES.cabinet_personalData}
                  element={
                    <AuthorizationGuard>
                      <PersonalData />
                    </AuthorizationGuard>
                  }
                />
                <Route
                  path={ROUTES.cabinet_address}
                  element={
                    <AuthorizationGuard>
                      <Address />
                    </AuthorizationGuard>
                  }
                />
                <Route
                  path={ROUTES.cabinet_events}
                  element={
                    <AuthorizationGuard>
                      <Events />
                    </AuthorizationGuard>
                  }
                />
                <Route
                  path={ROUTES.cabinet_subscriptions}
                  element={
                    <AuthorizationGuard>
                      <Subscription />
                    </AuthorizationGuard>
                  }
                />
                <Route
                  path={ROUTES.cabinet_paymentMethod}
                  element={
                    <AuthorizationGuard>
                      <PaymentPage />
                    </AuthorizationGuard>
                  }
                />
                <Route
                  path={ROUTES.cabinet_referral}
                  element={
                    <AuthorizationGuard requireVerified>
                      <ReferralProgram />
                    </AuthorizationGuard>
                  }
                />
                <Route
                  path={ROUTES.cabinet_referral_rewards}
                  element={company.match({
                    v3: <Navigate to="/cabinet/promocodes/referral" replace />,
                    default: (
                      <AuthorizationGuard requireVerified>
                        <RewardedPromocodes />
                      </AuthorizationGuard>
                    ),
                  })}
                />
                {company.match({
                  citydrinks: (
                    <Route path={ROUTES.cabinet_promocodes}>
                      <Route
                        index
                        element={
                          <AuthorizationGuard>
                            <Promocodes />
                          </AuthorizationGuard>
                        }
                      />
                      <Route
                        path={ROUTES.cabinet_promocodes_tab}
                        element={
                          <AuthorizationGuard>
                            <Promocodes />
                          </AuthorizationGuard>
                        }
                      />
                    </Route>
                  ),
                })}
              </Route>
            </>
          )}
          <Route path={ROUTES.search} element={<SearchResults />} />
          <Route path={ROUTES.legals} element={<Legals />} />

          {company.match({
            dubaiexpress: (
              <>
                <Route path={ROUTES.about} element={<AboutDE />} />
                <Route path={ROUTES.contacts} element={<ContactsDE />} />
                <Route path={ROUTES.faq} element={<FAQDE />} />
                <Route path={ROUTES.delivery} element={<DeliveryDE />} />
                <Route path={ROUTES.returns} element={<ReturnsDE />} />
              </>
            ),
            dubaiexpresscom: (
              <>
                <Route path={ROUTES.about} element={<AboutDE />} />
                <Route path={ROUTES.contacts} element={<ContactsDE />} />
                <Route path={ROUTES.faq} element={<FAQDE />} />
                <Route path={ROUTES.delivery} element={<DeliveryDE />} />
                <Route path={ROUTES.returns} element={<ReturnsDE />} />
              </>
            ),
            citydrinks: (
              <>
                <Route
                  path={ROUTES.shippingReturn}
                  element={<ShippingReturn />}
                />
                <Route path={ROUTES.refund} element={<Refund />} />
                <Route path={ROUTES.faq} element={<FAQ />} />
                <Route path={ROUTES.influencers} element={<Influencers />} />
                <Route path={ROUTES.careers} element={<Careers />} />
                <Route path={ROUTES.contactUs} element={<ContactUs />} />
                <Route path={ROUTES.brand} element={<Brand />} />
                <Route path={ROUTES.ourStory} element={<OurStory />} />
              </>
            ),
            okie: (
              <>
                <Route path={ROUTES.faq} element={<FaqOkie />} />
                <Route path={ROUTES.customerCare} element={<CustomerCare />} />
                <Route path={ROUTES.about} element={<About />} />
                <Route path={ROUTES.payment} element={<Payment />} />
              </>
            ),
          })}
          {import.meta.env.DEV && <Route path="/icons" element={<Icons />} />}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </LayoutComponent>
    </>
  );
});
